/**
 * 門市菜單 Service
 * 負責從 Google Sheets 取得門市菜單資料（使用 sheet_url）
 * 只包含：分類、餐點、套餐
 */

import axios from "axios";
import config from "../config";
import store from "../store/store";

// 創建 Axios 實例
const httpClient = axios.create({
    baseURL: config.googleSheetBaseUrl,
});

/**
 * 取得門市菜單的 Google Sheet ID
 * 從 storeInfo.sheet_url 取得（而非 sheet_url_scan）
 */
function _getStoreMenuSheetId() {
    const storeInfo = store.state.basic.storeInfo;
    const sheetUrl = storeInfo?.sheet_url || '';

    if (!sheetUrl) {
        console.warn('門市菜單 sheet_url 不存在');
        return '';
    }

    // 使用 regexp 取得 sheet id
    const sheetIdRegexp = /https:\/\/docs\.google\.com\/spreadsheets\/d\/([^/]+)/;
    const match = sheetUrl.match(sheetIdRegexp) || [];
    return match[1] || '';
}

/**
 * 取得門市菜單的 Metadata（包含所有 Sheet 名稱）
 */
function _getStoreMenuMetaData() {
    const googleSheetId = _getStoreMenuSheetId();

    if (!googleSheetId) {
        return Promise.reject(new Error('無法取得門市菜單 Sheet ID'));
    }

    return httpClient.get(`${googleSheetId}?key=${config.googleSheetKey}`);
}

/**
 * 從 Metadata 取得前三個 Sheet 的名稱
 * @returns {Promise<{categories: string, combos: string, products: string}>}
 */
async function _getStoreMenuSheets() {
    try {
        const response = await _getStoreMenuMetaData();
        const sheets = response?.data?.sheets || [];

        if (sheets.length < 3) {
            console.warn('門市菜單 Sheet 數量不足，需要至少 3 個 Sheet');
            return {
                categories: '',
                combos: '',
                products: ''
            };
        }

        // 取得前三個 Sheet 的名稱
        // [0] => 分類
        // [1] => 套餐
        // [2] => 餐點
        const sheetNames = {
            categories: sheets[0]?.properties?.title || '',
            combos: sheets[1]?.properties?.title || '',
            products: sheets[2]?.properties?.title || ''
        };

        console.log('門市菜單 Sheet 名稱:', sheetNames);
        return sheetNames;

    } catch (error) {
        console.error('取得門市菜單 Sheet 名稱失敗:', error);
        throw error;
    }
}

/**
 * 從門市菜單取得指定 Sheet 的資料
 * @param {string} sheet - Sheet 名稱
 */
function _getStoreMenuSheet(sheet) {
    const googleSheetId = _getStoreMenuSheetId();

    if (!googleSheetId) {
        return Promise.reject(new Error('無法取得門市菜單 Sheet ID'));
    }

    return httpClient.get(
        `${googleSheetId}/values/${sheet}?alt=json&key=${config.googleSheetKey}`
    );
}

// Response 攔截器
httpClient.interceptors.response.use(
    (response) => {
        // 成功處理 API 回應
        return response;
    },
    (error) => {
        // 處理錯誤
        console.error('門市菜單 API error:', error);
        const status = error?.response?.status;

        if (status) {
            console.error('門市菜單 API error, status:', status);

            if (status === 404) {
                console.error('門市菜單 Sheet 不存在或無法存取');
            } else if (status === 403) {
                console.error('門市菜單 Sheet 權限不足');
            }
        }

        return Promise.reject(error);
    }
);

// 匯出 API 方法
export const getStoreMenuMetaData = async () => _getStoreMenuMetaData();
export const getStoreMenuSheets = async () => _getStoreMenuSheets();
export const getStoreMenuSheetData = async (sheet) => _getStoreMenuSheet(sheet);
